import { useApi } from '../../context/ApiContextProvider';
import { useMerchantCabinetConfig } from '../../config/ConfigProvider';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';
import { usePagerPropsFromUrl } from '../pager/use-pager-props-from-url';
import { useUIDateFormat } from '../use-ui-dateFormat';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { groupByPredicate } from '@payler/utils';
import dayjs from 'dayjs';
import { useFasterPaymentsFiltersFromUrl } from './url';
import { usePagerUrlCallbacks } from '../pager/use-pager-url-callbacks';
import { DEFAULT_PAGER_PROPS } from '../../const';
import { PaginationProps } from '@payler/ui-components';
import { TFasterPaymentComputedHistoryItem } from './types';

const FASTER_PAYMENTS_STALE_TIME = 60 * 1000;
const FASTER_PAYMENT_DATA_STALE_TIME = 60 * 1000;
const FASTER_PAYMENTS_TEMPLATES_STALE_TIME = 600 * 1000;

export const useFasterPaymentsQuery = () => {
  const api = useApi();
  const { culture } = useMerchantCabinetConfig();
  const contractId = useURLContractId();
  const pagerData = usePagerPropsFromUrl();
  const filters = useFasterPaymentsFiltersFromUrl();
  const dateFormat = useUIDateFormat();
  if (!contractId) throw new Error('no contractId');
  return useQuery({
    queryKey: ['faster-payments', culture, contractId, pagerData, filters],
    queryFn: async () => {
      const data = await api.getFasterPayments(
        contractId,
        {
          ...filters,
          ...pagerData,
        },
        dateFormat,
      );

      const grouped = groupByPredicate(data.items, (v) => {
        const dateString = v['createdAt'] as string;
        return dayjs(dateString).format('YYYY-MM-DD');
      });
      return { ...data, grouped };
    },
    staleTime: FASTER_PAYMENTS_STALE_TIME,
  });
};

export const useFasterPaymentsQueryPagerProps = () => {
  const payments = useFasterPaymentsQuery();
  const { goToPage, setPageSize } = usePagerUrlCallbacks();
  if (payments.status !== 'success') {
    return { ...DEFAULT_PAGER_PROPS, goToPage, setPageSize } as PaginationProps;
  }
  const { page, pageSize, totalItems } = payments.data.paginationMetadata;
  return {
    goToPage,
    setPageSize,
    pageSize,
    currentPage: page,
    totalRecords: totalItems,
  } as PaginationProps;
};

export const useFasterPaymentDataQuery = (id: number) => {
  const api = useApi();
  const contractId = useURLContractId();
  if (!contractId) throw new Error('no contract id');
  const { culture } = useMerchantCabinetConfig();
  return useQuery({
    queryKey: ['faster-payments', culture, contractId, id],
    queryFn: async () => {
      const data = await api.getFasterPaymentWithHistory(contractId, id);
      const history: TFasterPaymentComputedHistoryItem[] = [];
      data.operations?.forEach((op) => {
        op.history?.forEach((h) => {
          history.push({
            type: op.type,
            typeDescription: op.typeDescription,
            ...h,
          });
        });
      });
      return {
        ...data,
        history: history.sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1)),
      };
    },
    staleTime: FASTER_PAYMENT_DATA_STALE_TIME,
  });
};

export const useFasterPaymentsTemplatesQuery = () => {
  const api = useApi();
  const contractId = useURLContractId();
  if (!contractId) throw new Error('no contract Id');
  return useSuspenseQuery({
    queryKey: ['faster-payments', 'templates', contractId],
    queryFn: async () => api.getFasterPaymentsTemplates(contractId),

    staleTime: FASTER_PAYMENTS_TEMPLATES_STALE_TIME,
  });
};
